import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import moment from 'moment';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { CustomTextConfigurationApi } from 'Api/CustomTextConfiguration/CustomTextConfigurationApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { FormFieldSelectOrCreate } from 'Components/FormField/FormFieldSelectOrCreate/FormFieldSelectOrCreate';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { InherentRiskQuestionnaireForm, InherentRiskQuestionnaireUserTypeAndData, getDataIsInsufficientForSubmittingIrqReason } from 'Components/UcrmTprm/InherentRiskQuestionnaireForm/InherentRiskQuestionnaireForm';
import { IrqTargetCompletionDateSelection, getDefaultIrqTargetCompletionDate, getIrqTargetCompletionDateSelectionProps } from 'Components/UcrmTprm/IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';
import { SubmitIrqSelections } from 'Components/UcrmTprm/SubmitIrqSelections/SubmitIrqSelections';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_SUBMIT } from 'Config/Icons';
import { TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING } from 'Config/PathTemplates';
import { ACTION_CREATED, ACTION_SAVED, ACTION_SKIPPED, ACTION_SUBMITTED, ASSESSMENT_SUBJECT_FORM_TEST_ID, IRQ_FORM_TEST_ID, ORGANIZATION_FORM_TEST_ID, SEARCH_PARAM_ASSESSMENT_SUBJECT_ACTIONS, SEARCH_PARAM_ASSESSMENT_SUBJECT_ID, SEARCH_PARAM_COMPLETING_INTAKE, SEARCH_PARAM_IRQ_ACTIONS, SEARCH_PARAM_ORGANIZATION_ACTIONS, SEARCH_PARAM_ORGANIZATION_ID, SEARCH_PARAM_WORKFLOW_STEP, SUBMIT_FORM_TEST_ID, SUCCESS_MESSAGE_IRQ, TPRM_SUCCESS_MESSAGE_SERVICE, TPRM_SUCCESS_MESSAGE_THIRD_PARTY } from 'Config/Text';
import { TPRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITH_RECOMMENDATION, TPRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_IRQ_STEP_SKIPPED, TPRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_SERVICE_STEP_SKIPPED, TPRM_INTAKE_SERVICE_ALREADY_FINALIZED, TPRM_INTAKE_SERVICE_ALREADY_SUBMITTED_TO_BE_FINALIZED, TPRM_INTAKE_SERVICE_CANNOT_BE_SUBMITTED_BECAUSE_SERVICE_NOT_CREATED, TPRM_INTAKE_SUBMIT_IRQ_CHECKBOX_TPRM_USER, TPRM_INTAKE_SUBMIT_SERVICE_CHECKBOX_NON_TPRM_USER, TPRM_INTAKE_SUBMIT_SERVICE_CHECKBOX_TPRM_USER, TPRM_INTAKE_SUBMIT_THIRD_PARTY_CHECKBOX_NON_TPRM_USER, TPRM_INTAKE_SUBMIT_THIRD_PARTY_CHECKBOX_TPRM_USER, TPRM_INTAKE_THIRD_PARTY_ALREADY_FINALIZED, TPRM_INTAKE_THIRD_PARTY_ALREADY_SUBMITTED_TO_BE_FINALIZED, TPRM_INTAKE_THIRD_PARTY_CANNOT_BE_SUBMITTED_BECAUSE_NON_TPRM_USER_IS_NOT_CREATOR, TPRM_THIRD_PARTY_NAME_SELECT_OR_CREATE } from 'Config/Tooltips';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { buildUrlForIntakeUrlState, getRecommendedDueDiligenceWorkflowDueDate } from 'Helpers/UcrmTprm';
import { TPRM_ROLES, UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AssessmentSubjectType, OrganizationSelectionOrCreationState, OrganizationType } from 'Models/AssessmentSubject';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceTimeframes, IntakeUrlState, IntakeWorkflowStep } from 'Models/DueDiligenceWorkflow';
import { InherentRiskQuestionnaireResponse } from 'Models/InherentRisk';
import { FinalizationState, userCanSubmitOrFinalizeDraft, userHasFullAccessOrCreatedDraft } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { CompleteTprmIntakeRequest, DEFAULT_TPRM_INTAKE_INSTRUCTIONS, ServiceForNonTprmUser, ThirdPartyForNonTprmUser, ThirdPartyResponse, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser, TprmEntities, TprmSubmitInherentRiskQuestionnaireSelections } from 'Models/TPRM';
import { OptionType } from 'Models/Types/GlobalType';

import styles from './TprmIntake.module.css';
import { ManageThirdPartyFormFields } from '../ManageThirdParty/Components/ManageThirdPartyFormFields/ManageThirdPartyFormFields';
import { ManageThirdPartyServiceFormFields } from '../ManageThirdPartyService/Components/ManageThirdPartyServiceFormFields/ManageThirdPartyServiceFormFields';

export interface TprmIntakeProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    authHandler: AuthHandler;
    customTextConfigurationApi: CustomTextConfigurationApi;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    issuesApi: IssuesApi;
    exceptionsApi: ExceptionsApi;
    tprmApi: ClientTPRMApi;
}

/**
 * Renders a four-step process for the intake of a new third party and/or third-party service:
 * * Create/edit a new third party or edit an existing third party
 * * Create/edit a new service or edit an existing service
 * * Edit the service's IRQ
 * * Submit drafted/saved items, if desired
 *
 * Can be used by a user with the TPRM role or a user without the TPRM role, the main difference in experience being that non-TPRM users cannot submit the IRQ. When a TPRM user submits a third party or service, it is finalized; for a non-TPRM user, it is submitted to be reviewed by the third party or service's manager.
 * Keeps state in sync with URL search params, such that progress can be resumed seamlessly if the page is reloaded. This is a common occurrence, since there is much to complete on this page, and the app automatically logs out after 15 minutes of inactivity.
 */
export const TprmIntake = (props: TprmIntakeProps) => {
    // Navigation utilities, and URL search params that stay in sync with the page's state.
    const location = useLocation();
    const navigate = useNavigate();

    // Users, which are used to display manager names if a non-TPRM user submits the third party and/or service to be finalized.
    const { users } = useCachedData();

    // Workflow state that is initially obtained from and then forever synchronized with the params in the URL. Facilitates intake workflow progress being maintained even if the page is refreshed, or if the user navigates here from the Third Parties and Services listing.
    const [urlState, setUrlState] = useState<IntakeUrlState>(() => {
        const searchParams = new URLSearchParams(location.search);

        const completingIntake = searchParams.get(SEARCH_PARAM_COMPLETING_INTAKE) === 'true';
        const step = parseInt(searchParams.get(SEARCH_PARAM_WORKFLOW_STEP) ?? '0') as IntakeWorkflowStep;
        const organizationId = searchParams.get(SEARCH_PARAM_ORGANIZATION_ID) ?? undefined;
        const assessmentSubjectId = searchParams.get(SEARCH_PARAM_ASSESSMENT_SUBJECT_ID) ?? undefined;

        const organizationActions = (searchParams.get(SEARCH_PARAM_ORGANIZATION_ACTIONS) ?? '').split('-');
        const assessmentSubjectActions = (searchParams.get(SEARCH_PARAM_ASSESSMENT_SUBJECT_ACTIONS) ?? '').split('-');
        const irqActions = (searchParams.get(SEARCH_PARAM_IRQ_ACTIONS) ?? '').split('-');

        const _urlState: IntakeUrlState = {
            completingIntake,
            step,
            organizationId,
            assessmentSubjectId,
            organizationActions: {
                created: organizationActions.includes(ACTION_CREATED),
                saved: organizationActions.includes(ACTION_SAVED),
                submitted: organizationActions.includes(ACTION_SUBMITTED),
            },
            assessmentSubjectActions: {
                skipped: assessmentSubjectActions.includes(ACTION_SKIPPED),
                created: assessmentSubjectActions.includes(ACTION_CREATED),
                saved: assessmentSubjectActions.includes(ACTION_SAVED),
                submitted: assessmentSubjectActions.includes(ACTION_SUBMITTED),
            },
            irqActions: {
                skipped: irqActions.includes(ACTION_SKIPPED),
                saved: irqActions.includes(ACTION_SAVED),
                submitted: irqActions.includes(ACTION_SUBMITTED),
            },
        };

        return _urlState;
    });

    // Memoized information about the current user.
    const userHasFullTprmAccess = useMemo(() => userHasAuthorizedRole(props.authHandler.getUserRoles(), TPRM_ROLES), [props.authHandler]);
    const userId = useMemo(() => props.authHandler.getAuthenticatedUserSubject()!, [props.authHandler]);

    // Data loaded initially to be used on the page.
    const [tprmIntakeInstructions, setTprmIntakeInstructions] = useState<string>();
    const [thirdParties, setThirdParties] = useState<ThirdPartyResponseWithServices[] | ThirdPartyWithServicesForNonTprmUser[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    // The "main data" of the page: the entities/selections that are made as the forms are completed.
    const [thirdPartySelectionOrCreationState, setThirdPartySelectionOrCreationState] = useState<OrganizationSelectionOrCreationState<TprmEntities>>();
    const [service, setService] = useState<AssessmentSubjectType<TprmEntities> | null>();
    const [assessmentConfigurations, setAssessmentsConfiguration] = useState<DueDiligenceAssessmentConfiguration[] | null>();
    const [irqAndValidation, setIrqAndValidation] = useState<{ irq?: InherentRiskQuestionnaireResponse; submitIrqValidationErrorMessage?: string; highlightMissingInformation?: boolean }>();
    const [timeframes, setTimeframes] = useState<DueDiligenceTimeframes | null>();

    // Toasts and dialogs.
    const [toastContent, setToastContent] = useState<{ variant: 'success' | 'failure'; text: string }>();
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState<boolean>(false);

    // States that are used to determine which checkboxes are checked/enabled in the final step of the page.
    const [thirdPartyCheckboxChecked, setThirdPartyCheckboxChecked] = useState(true);
    const [serviceCheckboxChecked, setServiceCheckboxChecked] = useState(true);
    const [irqCheckboxChecked, setIrqCheckboxChecked] = useState(userHasFullTprmAccess);

    // The selections made in the Submit step of the page.
    const [submitIrqSelections, setSubmitIrqSelections] = useState<TprmSubmitInherentRiskQuestionnaireSelections>({ assessment_selections: [], third_party_contacts: [] });
    // The user's selections for the IRQ target completion date and the due diligence workflow due date. When an override is undefined, the recommended date is used (and submitted) instead, so leaving a date picker untouched still sends the recommended default. The wrapper object distinguishes "the user has not made a selection" from "the user explicitly cleared the date".
    const [irqTargetCompletionDateOverride, setIrqTargetCompletionDateOverride] = useState<{ date: Date | undefined }>();
    const [dueDiligenceWorkflowDueDateOverride, setDueDiligenceWorkflowDueDateOverride] = useState<{ date: Date | undefined }>();

    /**
     * Keeps the validation message and checkbox enabled state in sync with the IRQ data.
     * This function _must_ be called whenever a new IRQ object is available.
     *
     * `checkIrqCheckboxIfAble` is used only when the IRQ is saved by the user, with the intention of saving them a click if they've completely filled out the IRQ before moving on to the Submit step.
     */
    const updateIrqAndSubmitIrqValidation = (irq?: InherentRiskQuestionnaireResponse, checkIrqCheckboxIfAble?: boolean) => {
        const submitIrqValidationErrorMessage = irq ? getDataIsInsufficientForSubmittingIrqReason(irq) : undefined;
        setIrqAndValidation((current) => ({ ...current, irq, submitIrqValidationErrorMessage }));

        if (submitIrqValidationErrorMessage) {
            setIrqCheckboxChecked(false);
        } else if (checkIrqCheckboxIfAble) {
            setIrqCheckboxChecked(true);
        }
    };

    /* Loads custom TPRM intake instructions (if they have been defined in Settings) when the component mounts. */
    useEffect(() => {
        const getCustomInstructions = async () => {
            try {
                const customTextConfiguration = (await props.customTextConfigurationApi.getCustomizedText()).data;
                setTprmIntakeInstructions(customTextConfiguration.tprm_intake_instructions ?? DEFAULT_TPRM_INTAKE_INSTRUCTIONS);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };
        getCustomInstructions();
    }, [props.customTextConfigurationApi]);

    /* Whenever relevant state changes, updates the URL search params that maintain progress on the page even through page reloads. */
    useEffect(() => {
        navigate(buildUrlForIntakeUrlState(ProductModule.TPRM, urlState), { replace: true });
    }, [navigate, urlState]);

    useEffect(() => {
        // Prevent re-fetching initial data.
        if (service !== undefined) {
            return;
        }

        const getServiceDetails = async (thirdPartyId: string, serviceId: string): Promise<void> => {
            try {
                if (userHasFullTprmAccess) {
                    const response = await props.tprmApi.getServiceDetails(thirdPartyId, serviceId);
                    setService(response.data);
                } else {
                    const response = await props.tprmApi.getServiceDetailsForNonTprmUser(thirdPartyId, serviceId);
                    setService(response.data);
                }
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (urlState.organizationId && urlState.assessmentSubjectId) {
            getServiceDetails(urlState.organizationId, urlState.assessmentSubjectId);
        } else {
            setService(null);
        }
    }, [urlState.organizationId, urlState.assessmentSubjectId, props.tprmApi, userHasFullTprmAccess, service]);

    useEffect(() => {
        // Prevent re-fetching initial data.
        if (irqAndValidation !== undefined) {
            return;
        }

        const getIrq = async (thirdPartyId: string, serviceId: string): Promise<void> => {
            try {
                const response = await props.tprmApi.getInherentRiskQuestionnaire(thirdPartyId, serviceId);
                updateIrqAndSubmitIrqValidation(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (urlState.organizationId && urlState.assessmentSubjectId) {
            getIrq(urlState.organizationId, urlState.assessmentSubjectId);
        } else {
            updateIrqAndSubmitIrqValidation(undefined);
        }
    }, [urlState.organizationId, urlState.assessmentSubjectId, props.tprmApi, userHasFullTprmAccess, irqAndValidation]);

    /*
     * Fetches all third parties when the component mounts.
     * * The list of third parties is used to form the drop-down options that allow the user to select an existing third party.
     * * The list is also used to search for the currently selected third party at page load time, if a third party was selected/created before the page was reloaded.
     */
    useEffect(() => {
        // Prevent prematurely searching data or re-fetching initial data.
        if (thirdPartySelectionOrCreationState !== undefined) {
            return;
        }

        const getThirdParties = async () => {
            try {
                let thirdParties: ThirdPartyResponseWithServices[] | ThirdPartyWithServicesForNonTprmUser[];

                if (userHasFullTprmAccess) {
                    thirdParties = (await props.tprmApi.getThirdParties()).data;
                    setThirdParties(thirdParties);
                } else {
                    thirdParties = (await props.tprmApi.getThirdPartiesForNonTprmUser()).data;
                    setThirdParties(thirdParties);
                }

                if (urlState.organizationId) {
                    const thirdParty = thirdParties.find((thirdParty) => thirdParty.id === urlState.organizationId)!;
                    setThirdPartySelectionOrCreationState({ type: 'savedOrganizationSelected', organization: thirdParty });
                } else {
                    setThirdPartySelectionOrCreationState({ type: 'notStarted' });
                }
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParties();
    }, [props.tprmApi, urlState.organizationId, thirdPartySelectionOrCreationState, userHasFullTprmAccess]);

    /*
     * Fetches due diligence assessments configuration when the component mounts, if the user has full access to TPRM.
     */
    useEffect(() => {
        const getAssessmentsConfiguration = async () => {
            try {
                const configurations = (await props.dueDiligenceAssessmentConfigurationApi.getAllAssessmentConfigurations()).data;
                setAssessmentsConfiguration(configurations);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (userHasFullTprmAccess) {
            getAssessmentsConfiguration();
        } else {
            setAssessmentsConfiguration(null);
        }
    }, [props.dueDiligenceAssessmentConfigurationApi, props.tprmApi, userHasFullTprmAccess]);

    useEffect(() => {
        const getTimeframes = async () => {
            try {
                const response = await props.dueDiligenceAssessmentConfigurationApi.getTimeframes();
                setTimeframes(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (userHasFullTprmAccess) {
            getTimeframes();
        } else {
            setTimeframes(null);
        }
    }, [props.dueDiligenceAssessmentConfigurationApi, userHasFullTprmAccess]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (thirdParties === undefined || thirdPartySelectionOrCreationState === undefined || service === undefined || irqAndValidation === undefined || tprmIntakeInstructions === undefined || timeframes === undefined) {
        return <Placeholder />;
    }

    const irqTargetCompletionInDays = timeframes?.expected_days_to_complete_irq;
    const irqTargetCompletionDateArgs = { action: 'startServiceAssessmentWorkflow' as const, currentAssessmentWorkflowIrqTargetCompletionDate: undefined, expectedIrqDurationInDays: irqTargetCompletionInDays };
    const recommendedIrqTargetCompletionDate = getDefaultIrqTargetCompletionDate(irqTargetCompletionDateArgs);
    const irqWillNeedToBeCompleted = !irqCheckboxChecked;
    const recommendedDueDiligenceWorkflowDueDate = assessmentConfigurations && timeframes ? getRecommendedDueDiligenceWorkflowDueDate(irqWillNeedToBeCompleted ? recommendedIrqTargetCompletionDate : undefined, assessmentConfigurations, timeframes) : undefined;

    // For each date, the user's explicit selection takes precedence; otherwise the recommended date is used (and submitted) by default.
    const irqTargetCompletionDate = irqTargetCompletionDateOverride ? irqTargetCompletionDateOverride.date : recommendedIrqTargetCompletionDate;
    const dueDiligenceWorkflowDueDate = dueDiligenceWorkflowDueDateOverride ? dueDiligenceWorkflowDueDateOverride.date : recommendedDueDiligenceWorkflowDueDate;

    const thirdPartyOptions: OptionType[] = thirdParties.map((thirdParty) => ({ value: thirdParty.id, label: thirdParty.name }));
    const thirdParty = thirdPartySelectionOrCreationState.type === 'savedOrganizationSelected' ? thirdPartySelectionOrCreationState.organization : undefined;
    const userCanSubmitThirdParty = !!thirdParty && userCanSubmitOrFinalizeDraft(userId, userHasFullTprmAccess, thirdParty);
    const userCanSubmitService = !!service && userCanSubmitOrFinalizeDraft(userId, userHasFullTprmAccess, service);

    const onRequestFailed = (errorMessage: string) => {
        setToastContent({ variant: 'failure', text: errorMessage });
    };

    const onThirdPartySaved = async (thirdParty: ThirdPartyResponse | ThirdPartyForNonTprmUser) => {
        setUrlState((current) => ({
            ...current,
            step: current.step === IntakeWorkflowStep.ORGANIZATION ? IntakeWorkflowStep.ASSESSMENT_SUBJECT : current.step,
            organizationId: thirdParty.id,
            organizationActions: {
                ...current.organizationActions,
                created: thirdPartySelectionOrCreationState.type !== 'savedOrganizationSelected' ? true : current.organizationActions.created,
                saved: true,
            },
        }));

        // TODO: It's quite hacky to hardcode `services` and use `as` below, even though we know:
        // - the operation of saving a third party can't change the list of services associated with the third party being created/updated in the first step of the page's workflow.
        // - creating a new service in the second step of the page's workflow won't/shouldn't impact the services listed for a third party that existed before this page was visited, since the point is to list preexisting services so the user doesn't create a duplicate. (A service created on this page is filtered out in the JSX below regardless.)
        // This hack is necessary because of the overly numerous interfaces for third parties that we have defined; the backend is not returning objects with a `services` property for the endpoints used to create/update a third party, and it doesn't seem straightforward to change this.
        setThirdPartySelectionOrCreationState((currentState) => {
            const currentThirdParty = currentState?.type === 'savedOrganizationSelected' ? currentState.organization : undefined;
            const currentServices = currentThirdParty?.services ?? [];

            if (userHasFullTprmAccess) {
                return { type: 'savedOrganizationSelected', organization: { ...thirdParty, services: currentServices as ThirdPartyService[] } as ThirdPartyResponseWithServices };
            } else {
                return { type: 'savedOrganizationSelected', organization: { ...thirdParty, services: currentServices as ServiceForNonTprmUser[] } as ThirdPartyWithServicesForNonTprmUser };
            }
        });

        setToastContent({ variant: 'success', text: TPRM_SUCCESS_MESSAGE_THIRD_PARTY });
    };

    const onServiceSaved = async (savedService: AssessmentSubjectType<TprmEntities>) => {
        try {
            if (!irqAndValidation.irq) {
                const irq = (await props.tprmApi.getInherentRiskQuestionnaire(savedService.vendor_id, savedService.id)).data!;
                updateIrqAndSubmitIrqValidation(irq);
            }
            setUrlState((current) => ({
                ...current,
                step: current.step === IntakeWorkflowStep.ASSESSMENT_SUBJECT ? IntakeWorkflowStep.IRQ : current.step,
                assessmentSubjectId: savedService.id,
                assessmentSubjectActions: {
                    ...current.assessmentSubjectActions,
                    created: !service ? true : current.assessmentSubjectActions.created,
                    saved: true,
                },
            }));
            setService(savedService);
            setToastContent({ variant: 'success', text: TPRM_SUCCESS_MESSAGE_SERVICE });
        } catch (error) {
            onRequestFailed(error.message);
        }
    };

    const onIrqSaved = async (updatedIrq: InherentRiskQuestionnaireResponse) => {
        updateIrqAndSubmitIrqValidation(updatedIrq, userHasFullTprmAccess);
        setUrlState((current) => ({
            ...current,
            step: IntakeWorkflowStep.CHOOSE_SUBMISSIONS,
            irqActions: { ...current.irqActions, saved: true },
        }));
        setToastContent({ variant: 'success', text: SUCCESS_MESSAGE_IRQ });
    };

    const expandedSteps: IntakeWorkflowStep[] = (() => {
        const _expandedStepsBeforeSkipsAccountedFor: IntakeWorkflowStep[] = (() => {
            switch (urlState.step) {
                case IntakeWorkflowStep.ORGANIZATION:
                    return [IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.ASSESSMENT_SUBJECT:
                    return [IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.IRQ:
                    return [IntakeWorkflowStep.IRQ, IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.CHOOSE_SUBMISSIONS:
                    return [IntakeWorkflowStep.CHOOSE_SUBMISSIONS, IntakeWorkflowStep.IRQ, IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.COMPLETE:
                    return [IntakeWorkflowStep.CHOOSE_SUBMISSIONS, IntakeWorkflowStep.IRQ, IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
            }
        })();

        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            return _expandedStepsBeforeSkipsAccountedFor;
        }

        const _expandedSteps = _expandedStepsBeforeSkipsAccountedFor.filter((step) => {
            if (urlState.assessmentSubjectActions.skipped) {
                return step !== IntakeWorkflowStep.ASSESSMENT_SUBJECT;
            }
            if (urlState.irqActions.skipped) {
                return step !== IntakeWorkflowStep.IRQ;
            }
            return true;
        });
        return _expandedSteps;
    })();

    /**
     * Returns either the third party form or a fragment. `key` _must_ be used for the form component, as the "purpose" of the form will change (create-->edit) once the third party is saved, and the form component expects to be rendered freshly for a given purpose.
     * A fragment is returned when either:
     * * the user has not selected an existing third party, or
     * * the user does not have full access to TPRM, and has selected a finalized third party that they are not allowed to edit. In this case, the third party's name will still be displayed in the selection drop-down, but the form for editing it will not be rendered.
     */
    const thirdPartyForm: JSX.Element = (() => {
        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const name = thirdParty!.name;
            const created = urlState.organizationActions.created;
            const edited = urlState.organizationActions.saved;

            let summaryString = '';
            if (created) {
                summaryString = `${name} was created.`;
            } else {
                summaryString = edited ? `${name} was selected and changes were saved.` : `${name} was selected.`;
            }

            return <Text noStyles>{summaryString}</Text>;
        }

        // If third party has been created and saved, or has been loaded as initial data for the page, then the user can only change the third party's name, in which case this element is not used and the third party name is managed by the child.
        // If a saved third party is not present, then the user can edit the name or select a different third party, in which case this element is used and the third party name is managed by this component.
        const thirdPartyNameField = (
            <div className={styles.formFieldContainer}>
                <FormFieldSelectOrCreate
                    required
                    tooltip={TPRM_THIRD_PARTY_NAME_SELECT_OR_CREATE}
                    formFieldId="selectOrCreateThirdParty"
                    formFieldLabel="Legal Name"
                    handleChange={(value, isCreateNew) => {
                        if (isCreateNew) {
                            setThirdPartySelectionOrCreationState({ type: 'newOrganizationNameEntered', name: value as string });
                        } else {
                            const thirdParty = thirdParties.find((thirdParty) => thirdParty.id === value);
                            if (thirdParty) {
                                setThirdPartySelectionOrCreationState({ type: 'savedOrganizationSelected', organization: thirdParty });
                                setUrlState((current) => ({
                                    ...current,
                                    step: IntakeWorkflowStep.ASSESSMENT_SUBJECT,
                                    organizationId: thirdParty.id,
                                }));
                            } else {
                                setThirdPartySelectionOrCreationState({ type: 'notStarted' });
                            }
                        }
                    }}
                    options={thirdPartyOptions}
                />
            </div>
        );

        switch (thirdPartySelectionOrCreationState?.type) {
            case 'savedOrganizationSelected':
                const userCanEditThirdParty = userHasFullAccessOrCreatedDraft(userId, userHasFullTprmAccess, thirdPartySelectionOrCreationState.organization);
                if (userCanEditThirdParty) {
                    const userCanSelectDifferentThirdParty = !service && !urlState.completingIntake && !urlState.organizationActions.created;
                    return (
                        <div data-testid={ORGANIZATION_FORM_TEST_ID}>
                            {userCanSelectDifferentThirdParty && thirdPartyNameField}
                            <ManageThirdPartyFormFields key="manageCreatedThirdParty" hideDeleteButton issuesApi={props.issuesApi} exceptionsApi={props.exceptionsApi} thirdParty={thirdPartySelectionOrCreationState.organization} userHasFullTprmAccess={userHasFullTprmAccess} tprmApi={props.tprmApi} name={userCanSelectDifferentThirdParty ? thirdPartySelectionOrCreationState.organization.name : undefined} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onThirdPartySaved} onFormSubmitFailed={onRequestFailed} />
                        </div>
                    );
                } else {
                    return <></>;
                }
            case 'newOrganizationNameEntered':
                return (
                    <div data-testid={ORGANIZATION_FORM_TEST_ID}>
                        {thirdPartyNameField}
                        <ManageThirdPartyFormFields key="createNewThirdParty" hideDeleteButton issuesApi={props.issuesApi} exceptionsApi={props.exceptionsApi} userHasFullTprmAccess={userHasFullTprmAccess} tprmApi={props.tprmApi} name={thirdPartySelectionOrCreationState.name} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onThirdPartySaved} onFormSubmitFailed={onRequestFailed} />
                    </div>
                );
            case 'notStarted':
                return <div data-testid={ORGANIZATION_FORM_TEST_ID}>{thirdPartyNameField}</div>;
            case undefined:
                return <></>;
        }
    })();

    /** Returns either the service form or a fragment. `key` _must_ be used for the form component, as the "purpose" of the form will change (create-->edit) once the service is saved, and the form component expects to be rendered freshly for a given purpose. */
    const serviceForm: JSX.Element = (() => {
        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const skipped = urlState.assessmentSubjectActions.skipped;
            const created = urlState.assessmentSubjectActions.created;
            const edited = urlState.assessmentSubjectActions.saved;

            let summaryString = '';
            if (skipped) {
                summaryString = 'This step was skipped.';
            } else if (created) {
                summaryString = `${service!.name} was created.`;
            } else {
                summaryString = edited ? `${service!.name} was selected and changes were saved.` : `${service!.name} was selected.`;
            }

            return <Text noStyles>{summaryString}</Text>;
        }

        const otherServices = thirdPartySelectionOrCreationState.type === 'savedOrganizationSelected' ? thirdPartySelectionOrCreationState.organization.services.filter((otherService) => otherService.id !== service?.id) : [];
        const existingServicesList =
            thirdPartySelectionOrCreationState.type === 'savedOrganizationSelected' && otherServices.length > 0 ? (
                <>
                    <div className={styles.existingServices}>
                        <Text variant="Header3">Existing Services</Text>
                        <ul className={styles.list}>
                            {[...otherServices]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((service) => (
                                    <li key={service.id}>
                                        <Text noStyles>{service.name}</Text>
                                    </li>
                                ))}
                        </ul>
                    </div>
                    <Text variant="Header3">New Service</Text>
                </>
            ) : (
                <></>
            );

        if (thirdPartySelectionOrCreationState.type !== 'savedOrganizationSelected') {
            return <></>;
        } else if (!!service) {
            return (
                <div data-testid={ASSESSMENT_SUBJECT_FORM_TEST_ID}>
                    {existingServicesList}
                    <ManageThirdPartyServiceFormFields key="manageCreatedService" artificialIntelligenceApi={props.artificialIntelligenceApi} service={service} hideDeleteButton userHasFullTprmAccess={userHasFullTprmAccess} tprmApi={props.tprmApi} thirdParty={thirdPartySelectionOrCreationState.organization} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onServiceSaved} onFormSubmitFailed={onRequestFailed} />
                </div>
            );
        } else {
            return (
                <div data-testid={ASSESSMENT_SUBJECT_FORM_TEST_ID}>
                    {existingServicesList}
                    <ManageThirdPartyServiceFormFields key="createNewService" artificialIntelligenceApi={props.artificialIntelligenceApi} hideDeleteButton userHasFullTprmAccess={userHasFullTprmAccess} tprmApi={props.tprmApi} thirdParty={thirdPartySelectionOrCreationState.organization} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onServiceSaved} onFormSubmitFailed={onRequestFailed} />
                </div>
            );
        }
    })();

    const irqForm: JSX.Element = (() => {
        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const skipped = urlState.irqActions.skipped;
            const edited = urlState.irqActions.saved;

            let summaryString = '';
            if (skipped) {
                summaryString = 'This step was skipped.';
            } else {
                summaryString = edited ? 'Changes were saved.' : 'No changes were saved.';
            }

            return <Text noStyles>{summaryString}</Text>;
        }

        const irq = irqAndValidation.irq;
        if (thirdPartySelectionOrCreationState.type === 'savedOrganizationSelected' && service && irq) {
            const userTypeAndData: InherentRiskQuestionnaireUserTypeAndData = userHasFullTprmAccess ? { userHasFullAccess: true, historical: false, assessmentConfigurations: assessmentConfigurations!, defaultIrq: irq, assessmentSubject: service as ThirdPartyService } : { userHasFullAccess: false, historical: false, defaultIrq: irq, assessmentSubject: service as ServiceForNonTprmUser };
            return (
                <div data-testid={IRQ_FORM_TEST_ID}>
                    <InherentRiskQuestionnaireForm artificialIntelligenceApi={props.artificialIntelligenceApi} documentApi={props.documentApi} highlightMissingInformation={irqAndValidation.highlightMissingInformation} module={ProductModule.TPRM} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmitSucceeded={onIrqSaved} onFormSubmitFailed={onRequestFailed} moduleApi={props.tprmApi} userTypeAndData={userTypeAndData} />
                </div>
            );
        } else {
            return <></>;
        }
    })();

    // The checkboxes are checked by default and remain checked (but disabled) for items that cannot be submitted, such as an item that a user without full access already submitted to be finalized, so whether the user can submit each item must also be checked here.
    const thirdPartyWillBeSubmitted = userCanSubmitThirdParty && thirdPartyCheckboxChecked;
    const serviceWillBeSubmitted = userCanSubmitService && serviceCheckboxChecked;
    const irqWillBeSubmitted = irqCheckboxChecked;

    const chooseSubmissionsForm: JSX.Element = (() => {
        if (!thirdParty) {
            return <></>;
        }

        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const serviceWasSkipped = urlState.assessmentSubjectActions.skipped;
            const irqWasSkipped = urlState.irqActions.skipped;
            const skippedText = (() => {
                if (serviceWasSkipped) {
                    return 'Because the third-party service and inherent risk questionnaire were skipped,';
                } else if (irqWasSkipped) {
                    return 'Because the inherent risk questionnaire was skipped,';
                } else {
                    return undefined;
                }
            })();

            return (
                <>
                    {urlState.organizationActions.submitted && <Text>{`${thirdParty.name} was ${userHasFullTprmAccess ? 'finalized.' : 'submitted to be finalized.'}${userHasFullTprmAccess ? '' : ` ${getUserNameFromSubject(thirdParty.vendor_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} has been notified.`}`}</Text>}
                    {urlState.assessmentSubjectActions.submitted && service && <Text>{`${service.name} was ${userHasFullTprmAccess ? 'finalized.' : 'submitted to be finalized.'}${userHasFullTprmAccess ? ' The due diligence workflow has been started.' : ` ${getUserNameFromSubject(service.vendor_service_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} has been notified.`}`}</Text>}
                    {userHasFullTprmAccess && urlState.irqActions.submitted && <Text>The inherent risk questionnaire was submitted. The due diligence questionnaires, due diligence assessments, and final review are available for changes.</Text>}
                    {userHasFullTprmAccess && !urlState.irqActions.submitted && skippedText === undefined && <Text>The inherent risk questionnaire was not submitted. The due diligence questionnaires, due diligence assessments, and final review are not available for changes.</Text>}
                    {userHasFullTprmAccess && !urlState.irqActions.submitted && skippedText !== undefined && <Text>{`${skippedText} the due diligence questionnaires, assessments, and final review are not available for changes.`}</Text>}
                </>
            );
        }

        const submitIrqValidationErrorMessage = irqAndValidation.submitIrqValidationErrorMessage;

        const thirdPartyTooltip: string = (() => {
            if (userHasFullTprmAccess) {
                return thirdParty.finalization_state === FinalizationState.FINALIZED ? TPRM_INTAKE_THIRD_PARTY_ALREADY_FINALIZED : TPRM_INTAKE_SUBMIT_THIRD_PARTY_CHECKBOX_TPRM_USER;
            } else {
                switch (thirdParty.finalization_state) {
                    case FinalizationState.DRAFT_IN_PROGRESS:
                        return userCanSubmitThirdParty ? TPRM_INTAKE_SUBMIT_THIRD_PARTY_CHECKBOX_NON_TPRM_USER : TPRM_INTAKE_THIRD_PARTY_CANNOT_BE_SUBMITTED_BECAUSE_NON_TPRM_USER_IS_NOT_CREATOR;
                    case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                        return TPRM_INTAKE_THIRD_PARTY_ALREADY_SUBMITTED_TO_BE_FINALIZED;
                    case FinalizationState.FINALIZED:
                        return TPRM_INTAKE_THIRD_PARTY_ALREADY_FINALIZED;
                }
            }
        })();

        const serviceTooltip: string = (() => {
            if (service === null) {
                return TPRM_INTAKE_SERVICE_CANNOT_BE_SUBMITTED_BECAUSE_SERVICE_NOT_CREATED;
            } else if (userHasFullTprmAccess) {
                return service.finalization_state === FinalizationState.FINALIZED ? TPRM_INTAKE_SERVICE_ALREADY_FINALIZED : TPRM_INTAKE_SUBMIT_SERVICE_CHECKBOX_TPRM_USER;
            } else {
                switch (service.finalization_state) {
                    case FinalizationState.DRAFT_IN_PROGRESS:
                        return TPRM_INTAKE_SUBMIT_SERVICE_CHECKBOX_NON_TPRM_USER;
                    case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                        return TPRM_INTAKE_SERVICE_ALREADY_SUBMITTED_TO_BE_FINALIZED;
                    case FinalizationState.FINALIZED:
                        return TPRM_INTAKE_SERVICE_ALREADY_FINALIZED;
                }
            }
        })();

        const irqTooltip: string = (() => {
            if (urlState.assessmentSubjectActions.skipped) {
                return urlState.irqActions.skipped ? TPRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_IRQ_STEP_SKIPPED : TPRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_SERVICE_STEP_SKIPPED;
            } else if (urlState.irqActions.skipped) {
                return TPRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_IRQ_STEP_SKIPPED;
            } else {
                return `${TPRM_INTAKE_SUBMIT_IRQ_CHECKBOX_TPRM_USER}${submitIrqValidationErrorMessage ? ` ${submitIrqValidationErrorMessage}` : ''}`;
            }
        })();

        return (
            <div data-testid={SUBMIT_FORM_TEST_ID}>
                <Checkbox
                    checked={thirdPartyCheckboxChecked}
                    disabled={!userCanSubmitThirdParty}
                    label={userHasFullTprmAccess ? 'Finalize third party' : 'Submit third party to be finalized'}
                    onToggled={(isChecked) => {
                        setThirdPartyCheckboxChecked(isChecked);
                        if (!isChecked) {
                            setServiceCheckboxChecked(false);
                            setIrqCheckboxChecked(false);
                            setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: false }));
                        }
                    }}
                    tooltip={thirdPartyTooltip}
                />
                <Checkbox
                    checked={serviceCheckboxChecked}
                    disabled={!userCanSubmitService}
                    label={userHasFullTprmAccess ? 'Finalize third-party service' : 'Submit third-party service to be finalized'}
                    onToggled={(isChecked) => {
                        setServiceCheckboxChecked(isChecked);
                        if (isChecked) {
                            setThirdPartyCheckboxChecked(true);
                        } else {
                            setIrqCheckboxChecked(false);
                            setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: false }));
                        }
                    }}
                    tooltip={serviceTooltip}
                />
                {userHasFullTprmAccess && (
                    <Checkbox
                        checked={irqCheckboxChecked}
                        disabled={urlState.irqActions.skipped}
                        label="Submit inherent risk questionnaire"
                        onToggled={(isChecked) => {
                            setIrqCheckboxChecked(isChecked);
                            if (isChecked) {
                                setThirdPartyCheckboxChecked(true);
                                setServiceCheckboxChecked(true);
                            } else {
                                setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: false }));
                            }
                        }}
                        tooltip={irqTooltip}
                    />
                )}
                <div className={styles.submitSelectionsContainer}>
                    {serviceWillBeSubmitted && !irqWillBeSubmitted && userHasFullTprmAccess && <IrqTargetCompletionDateSelection {...getIrqTargetCompletionDateSelectionProps(ProductModule.TPRM, irqTargetCompletionDateArgs, (date: Date | undefined) => setIrqTargetCompletionDateOverride({ date }))} />}
                    {serviceWillBeSubmitted && userHasFullTprmAccess && (
                        <div>
                            <FormFieldDatePicker dateFormat="MM/dd/yyyy" selected={dueDiligenceWorkflowDueDate} handleChange={(date: Date | undefined | null) => setDueDiligenceWorkflowDueDateOverride({ date: date ?? undefined })} formFieldId="dueDiligenceWorkflowDueDate" formFieldLabel="Due Diligence Workflow Due Date" placeholder="MM/DD/YYYY" tooltip={TPRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITH_RECOMMENDATION} />
                            {recommendedDueDiligenceWorkflowDueDate && <Text color="darkGray">Recommended: {moment(recommendedDueDiligenceWorkflowDueDate).format('MM/DD/YYYY')}</Text>}
                        </div>
                    )}
                    {irqWillBeSubmitted && service && assessmentConfigurations && userHasFullTprmAccess && submitIrqValidationErrorMessage === undefined && (
                        <div className={styles.submitIrqSelections}>
                            <SubmitIrqSelections assessments={assessmentConfigurations} contacts={service.vendor_contacts} module={ProductModule.TPRM} inherentRiskRating={irqAndValidation.irq?.inherent_risk_score ?? RiskRating.UNRATED} onSelectionsChanged={setSubmitIrqSelections} />
                        </div>
                    )}
                </div>
                <div className={styles.completeButtonContainer}>
                    <Button
                        variant="primary"
                        onClick={() => {
                            // The IRQ is treated uniquely because it tends to be lengthy and difficult to scan visually for missing information. The IRQ checkbox can be checked even if the user can't actually submit the IRQ. If the user tries to submit the IRQ without completing it, highlight missing information for the user.
                            if (irqWillBeSubmitted && submitIrqValidationErrorMessage) {
                                setToastContent({ variant: 'failure', text: submitIrqValidationErrorMessage });
                                setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: true }));
                            } else {
                                setToastContent(undefined);
                                setDisplayConfirmationModal(true);
                            }
                        }}
                        isLoading={false}
                        loadingText="Completing..."
                        fontAwesomeImage={ICON_SUBMIT}
                    >
                        Submit
                    </Button>
                </div>
            </div>
        );
    })();

    const confirmationModal = (() => {
        if (!displayConfirmationModal || thirdParty === undefined || service === undefined) {
            return <></>;
        }

        return (
            <ConfirmCompleteFormModal
                // Commenting to force newlines.
                dueDiligenceWorkflowDueDate={dueDiligenceWorkflowDueDate}
                hideModal={() => setDisplayConfirmationModal(false)}
                irqTargetCompletionDate={irqTargetCompletionDate}
                onSuccess={() =>
                    setUrlState((current) => ({
                        ...current,
                        step: IntakeWorkflowStep.COMPLETE,
                        organizationActions: { ...current.organizationActions, submitted: thirdPartyWillBeSubmitted },
                        assessmentSubjectActions: { ...current.assessmentSubjectActions, submitted: serviceWillBeSubmitted },
                        irqActions: { ...current.irqActions, submitted: irqWillBeSubmitted },
                    }))
                }
                service={service}
                submitIrqSelections={submitIrqSelections}
                thirdPartyWillBeSubmitted={thirdPartyWillBeSubmitted}
                serviceWillBeSubmitted={serviceWillBeSubmitted}
                irqWillBeSubmitted={irqWillBeSubmitted}
                thirdParty={thirdParty}
                tprmApi={props.tprmApi}
                userHasFullTprmAccess={userHasFullTprmAccess}
            />
        );
    })();

    // If a vertical stepper is ever needed elsewhere in the app, consider extracting the structure of this stepper into a reusable component, replacing/augmenting the existing `Stepper` to support horizontal and vertical steppers. The existing `Stepper` doesn't really do anything except standardize some styling.
    return (
        <>
            {toastContent && <TextToast clearToast={() => setToastContent(undefined)} {...toastContent} />}
            {confirmationModal}

            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(TPRM_THIRD_PARTIES_AND_SERVICES_LISTING)}>Third Parties and Services</BreadcrumbLink>
                        <BreadcrumbText>Create</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Create"
                headerDescription={tprmIntakeInstructions}
                body={[
                    {
                        content: (
                            <Stepper
                                activeStep={urlState.step}
                                orientation="vertical"
                                sx={{
                                    '& .MuiStepIcon-root': {
                                        // All of these colors are dark blue because the user can move freely between steps (once they are "unlocked" by creating prerequisite items), and the step indicators are never changed from step numbers to checkmarks.
                                        color: 'var(--hps-dark-blue)', // The color of the "step" circle before the step is active or completed.
                                        '&.Mui-active': {
                                            color: 'var(--hps-dark-blue)', // The color of the "step" circle when the step is active (this corresponds to `activeStep` above).
                                        },
                                        '&.Mui-completed': {
                                            color: 'var(--hps-dark-blue)', // The color of the "step" circle when the step is completed.
                                        },
                                    },
                                    '& .MuiStepContent-root': {
                                        borderColor: 'var(--hps-gray)', // The color of the left border of the step content area, which functions as part of the connecting line between steps.
                                    },
                                    '& .MuiStepConnector-line': {
                                        borderColor: 'var(--hps-gray)', // The color of the left border of the vertical space between steps, which functions as part of the connecting line between steps.
                                    },
                                }}
                            >
                                {/* Note: All steps are hardcoded as not completed so that the step indicators are never changed from step numbers to checkmarks. (The user can move freely between the steps; checkmarks may be confusing.) */}
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.ORGANIZATION)} completed={false}>
                                    <StepLabel>
                                        <Text noStyles variant="Header2">
                                            Third Party
                                        </Text>
                                    </StepLabel>
                                    <StepContent>{thirdPartyForm}</StepContent>
                                </Step>
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.ASSESSMENT_SUBJECT)} completed={false}>
                                    <StepLabel>
                                        <div className={styles.stepLabelContainer}>
                                            <Text noStyles variant="Header2">
                                                Third-Party Service
                                            </Text>
                                            {urlState.step === IntakeWorkflowStep.ASSESSMENT_SUBJECT && userCanSubmitThirdParty && (
                                                <Button
                                                    variant="linkText"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUrlState((current) => ({
                                                            ...current,
                                                            step: IntakeWorkflowStep.CHOOSE_SUBMISSIONS,
                                                            assessmentSubjectActions: {
                                                                ...current.assessmentSubjectActions,
                                                                skipped: true,
                                                            },
                                                            irqActions: {
                                                                ...current.irqActions,
                                                                skipped: true,
                                                            },
                                                        }));
                                                        setServiceCheckboxChecked(false);
                                                        setIrqCheckboxChecked(false);
                                                    }}
                                                >
                                                    Skip this step
                                                </Button>
                                            )}
                                        </div>
                                    </StepLabel>
                                    <StepContent>{serviceForm}</StepContent>
                                </Step>
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.IRQ)} completed={false}>
                                    <StepLabel>
                                        <div className={styles.stepLabelContainer}>
                                            <Text noStyles variant="Header2">
                                                Inherent Risk Questionnaire
                                            </Text>
                                            {urlState.step === IntakeWorkflowStep.IRQ && (
                                                <Button
                                                    variant="linkText"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUrlState((current) => ({
                                                            ...current,
                                                            step: IntakeWorkflowStep.CHOOSE_SUBMISSIONS,
                                                            irqActions: {
                                                                ...current.irqActions,
                                                                skipped: true,
                                                            },
                                                        }));
                                                        setIrqCheckboxChecked(false);
                                                    }}
                                                >
                                                    Skip this step
                                                </Button>
                                            )}
                                        </div>
                                    </StepLabel>
                                    <StepContent>{irqForm}</StepContent>
                                </Step>
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.CHOOSE_SUBMISSIONS)} completed={false}>
                                    <StepLabel>
                                        <div className={styles.stepLabelContainer}>
                                            <Text noStyles variant="Header2">
                                                Submit
                                            </Text>
                                            {urlState.step === IntakeWorkflowStep.CHOOSE_SUBMISSIONS && (urlState.assessmentSubjectActions.skipped || urlState.irqActions.skipped) && (
                                                <Button
                                                    variant="linkText"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUrlState((current) => ({
                                                            ...current,
                                                            step: current.assessmentSubjectActions.skipped ? IntakeWorkflowStep.ASSESSMENT_SUBJECT : IntakeWorkflowStep.IRQ,
                                                            assessmentSubjectActions: {
                                                                ...current.assessmentSubjectActions,
                                                                skipped: false,
                                                            },
                                                            irqActions: {
                                                                ...current.irqActions,
                                                                skipped: false,
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    Return to skipped steps
                                                </Button>
                                            )}
                                        </div>
                                    </StepLabel>
                                    <StepContent>{chooseSubmissionsForm}</StepContent>
                                </Step>
                            </Stepper>
                        ),
                    },
                ]}
            />
        </>
    );
};

interface ConfirmCompleteFormModalProps {
    hideModal: () => void;
    irqWillBeSubmitted: boolean;
    onSuccess: () => void;
    serviceWillBeSubmitted: boolean;
    submitIrqSelections: TprmSubmitInherentRiskQuestionnaireSelections;
    thirdParty: OrganizationType<TprmEntities>;
    thirdPartyWillBeSubmitted: boolean;
    tprmApi: ClientTPRMApi;
    userHasFullTprmAccess: boolean;

    dueDiligenceWorkflowDueDate?: Date;
    irqTargetCompletionDate?: Date;
    service: AssessmentSubjectType<TprmEntities> | null;
}

const ConfirmCompleteFormModal = (props: ConfirmCompleteFormModalProps) => {
    const { users } = useCachedData();

    const atLeastOneItemWillBeSubmitted = props.thirdPartyWillBeSubmitted || props.serviceWillBeSubmitted || props.irqWillBeSubmitted;

    return (
        <ConfirmationModal
            operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE}
            headerText="Submit Form"
            buttonText="Submit"
            buttonIcon={ICON_SUBMIT}
            buttonLoadingText="Submitting..."
            areYouSureText={atLeastOneItemWillBeSubmitted ? 'Are you sure you want to submit the selected items?' : 'Are you sure you want to submit the form?'}
            hideModal={props.hideModal}
            performOperation={async () => {
                const thirdPartyId = props.thirdParty.id;
                const serviceId = props.service?.id;

                const request: CompleteTprmIntakeRequest = {
                    submit_organization: props.thirdPartyWillBeSubmitted,
                    submit_assessment_subject: props.serviceWillBeSubmitted,
                    submit_irq: props.irqWillBeSubmitted,
                    submit_irq_selections: props.submitIrqSelections,
                    organization_id: thirdPartyId,
                    assessment_subject_id: serviceId,
                    due_diligence_workflow_due_date: props.dueDiligenceWorkflowDueDate ? jsDateToIso8601(props.dueDiligenceWorkflowDueDate) : undefined,
                    irq_target_completion_date: props.irqTargetCompletionDate ? jsDateToIso8601(props.irqTargetCompletionDate) : undefined,
                };

                await props.tprmApi.completeTprmIntake(request);
                props.onSuccess();

                const finalizedServiceId: string | undefined = props.service && props.serviceWillBeSubmitted ? props.service.id : undefined;

                return (
                    <>
                        <Text>Form submitted.</Text>
                        <Link size="sm" to={props.userHasFullTprmAccess && finalizedServiceId ? generatePath(TPRM_SERVICE_DASHBOARD, { third_party_id: thirdPartyId, service_id: finalizedServiceId }) : generatePath(TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, {})}>
                            {props.userHasFullTprmAccess && finalizedServiceId ? 'View service dashboard' : 'Return to third parties and third-party services'}
                        </Link>
                    </>
                );
            }}
        >
            {!atLeastOneItemWillBeSubmitted && <Text>Because new items were not selected to be submitted, new items will remain as drafts.</Text>}
            {props.thirdPartyWillBeSubmitted && <Text>{props.userHasFullTprmAccess ? 'The third party will no longer be a draft.' : `The third party will no longer be a draft, and ${getUserNameFromSubject(props.thirdParty.vendor_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} will be notified.`}</Text>}
            {props.serviceWillBeSubmitted && props.service && <Text>{props.userHasFullTprmAccess ? 'The third-party service will no longer be a draft, and the due diligence workflow will be started.' : `The third-party service will no longer be a draft, and ${getUserNameFromSubject(props.service.vendor_service_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} will be notified.`}</Text>}
            {props.irqWillBeSubmitted && <Text>Submitting the inherent risk questionnaire will make the due diligence questionnaires, due diligence assessments, and final review available for changes.</Text>}
        </ConfirmationModal>
    );
};
